import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { LeaderboardRow } from '@sorare/core/src/components/layout/LeaderboardRow';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { playerDetails } from '@sorare/core/src/lib/glossary';

import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { colorPerBonusRating } from 'lib/bonus';

import type { RankingLeaderboardRow_so5Ranking } from './__generated__/index.graphql';

const AvatarContainer = styled.div`
  position: relative;
`;
const StatusContainer = styled.div`
  position: absolute;
  display: flex;
  left: 0;
  top: 0;
  transform: translate(-3px, -3px);
`;
const Infos = styled(Vertical).attrs({ gap: 0 })`
  align-items: end;
`;

type Props = {
  so5Ranking: RankingLeaderboardRow_so5Ranking;
  isMyRanking?: boolean;
  onClick?: () => void;
};

export const RankingLeaderboardRow = ({
  so5Ranking,
  isMyRanking,
  onClick,
}: Props) => {
  const { ranking, score, eligibleDivisionChange, so5Lineup } = so5Ranking;
  const { bonusPoints, bonusRating, user, name, managerTeam } = so5Lineup;

  const teamName = managerTeam?.name || name;
  const bonusColor = bonusRating ? colorPerBonusRating[bonusRating] : undefined;

  return (
    <LeaderboardRow
      highlighted={isMyRanking}
      rank={ranking}
      user={user}
      avatar={
        <AvatarContainer>
          {eligibleDivisionChange &&
            eligibleDivisionChange !== DivisionChange.STABLE && (
              <StatusContainer>
                <DumbStatus divisionChange={eligibleDivisionChange} size={2} />
              </StatusContainer>
            )}
          <Avatar user={user} size={4} rounded />
        </AvatarContainer>
      }
      caption={<LabelS color="var(--c-nd-600)">{teamName}</LabelS>}
      onClick={onClick}
    >
      <Infos>
        <Horizontal gap={0.5} as="span">
          <LabelM bold>
            <FormattedNumber value={score} />
          </LabelM>
          <LabelXS uppercase color="var(--c-nd-800)" bold>
            <FormattedMessage {...playerDetails.points} />
          </LabelXS>
        </Horizontal>
        <Horizontal gap={0.5}>
          <CollectionArrow size={1} color={bonusColor} />
          <LabelS bold color={bonusColor}>
            +
            <FormattedNumber value={bonusPoints || 0} />
          </LabelS>
          <LabelXS uppercase bold color={bonusColor}>
            <FormattedMessage {...playerDetails.points} />
          </LabelXS>
        </Horizontal>
      </Infos>
    </LeaderboardRow>
  );
};

RankingLeaderboardRow.fragments = {
  so5Ranking: gql`
    fragment RankingLeaderboardRow_so5Ranking on So5Ranking {
      id
      ranking
      eligibleDivisionChange
      score
      so5Lineup {
        id
        name
        bonusPoints
        bonusRating
        managerTeam {
          id
          name
        }
        user {
          slug
          ...Avatar_publicUserInfoInterface
          ...LeaderboardRow_publicUserInfoInterface
        }
      }
    }
    ${LeaderboardRow.fragments.publicUserInfoInterface}
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<RankingLeaderboardRow_so5Ranking>,
};
